import { useEffect, useRef } from 'react'
import { MdClose, MdHelpOutline } from 'react-icons/md'

export type MarketingHelpTopic =
    | 'comparison'
    | 'leads'
    | 'channel'
    | 'sales'
    | 'funnel'
    | 'won'
    | 'attendance'
    | 'investment'
    | 'ads'

interface HelpSection {
    id: MarketingHelpTopic
    title: string
    what: string
    date: string
    why?: string
}

/**
 * O que cada número do dashboard de marketing soma, por qual data e por quê. Mesmo
 * formato do "?" do dashboard financeiro: a regra mora na tela.
 */
const SECTIONS: HelpSection[] = [
    {
        id: 'comparison',
        title: 'Comparação entre períodos',
        what:
            'Cada número mostra a variação contra o período comparado: seta para cima é alta, para baixo é queda. A cor diz se foi bom ou ruim — tempo de resposta e conversas sem resposta subindo aparecem em vermelho. "Novo" é quando o período comparado não tinha nada.',
        date:
            'Período anterior: num período "até hoje" (mês, semana, trimestre, ano), é o mesmo trecho do período anterior — 1º a 6 de outubro contra 1º a 6 de setembro. No personalizado, os mesmos dias logo antes. Ano anterior: as mesmas datas um ano antes.',
        why:
            'Comparar o começo de um mês com o fim do outro mistura efeitos de calendário (dia de pagamento, feriado) com o efeito da campanha.',
    },
    {
        id: 'leads',
        title: 'Leads',
        what:
            'Leads de aquisição criados no período. Fica de fora o lead que o próprio agendamento cria quando o cliente não tinha lead (balcão, paciente antigo): ele nasce no instante da venda e não diz de onde o cliente veio. "De anúncio" são os que chegaram com rastro de anúncio (Meta, Google ou TikTok).',
        date: 'Data de criação do lead.',
        why:
            'Anúncio que leva a um número conectado pelo WhatsApp Web não deixa rastro: o lead chega como WhatsApp orgânico. Só o número conectado pela API oficial recebe a identificação do anúncio.',
    },
    {
        id: 'channel',
        title: 'Canal de origem',
        what:
            'O canal que trouxe o lead na PRIMEIRA vez (primeiro toque). O rastro do anúncio vence a origem declarada: lead de WhatsApp que veio de um anúncio da Meta é Meta Ads. "Campanha própria" é o link rastreado do sistema. "Sem lead" é venda de cliente que nunca passou como lead (balcão, cadastro direto).',
        date: 'A origem é a do lead, não muda com o período.',
    },
    {
        id: 'sales',
        title: 'Vendas e receita atribuída',
        what:
            'As mesmas vendas da tabela "O que foi vendido" do dashboard financeiro (consultas, exames e procedimentos, sem orçamento e cancelado), cada uma atribuída ao canal do PRIMEIRO lead do cliente. Quando a venda não tem cliente ligado a lead, vale o lead da oportunidade. O filtro "Origem" no topo recorta tudo: em "Tráfego pago" (o padrão) só entram as vendas de quem chegou por anúncio, que é o resultado do time de marketing; "Todos" soma o orgânico e bate com o financeiro.',
        date: 'Data da venda: aprovação, ou o lançamento quando já nasceu agendado.',
        why:
            'O total bate com o financeiro por construção. A venda vai para o primeiro lead porque o agendamento às vezes cria outro lead para o mesmo cliente, e esse segundo lead não sabe de onde o cliente veio.',
    },
    {
        id: 'won',
        title: 'Oportunidades ganhas',
        what:
            'Leads com oportunidade marcada como ganha no período, e a soma do valor dessas oportunidades. Conta o lead uma vez, mesmo que ele tenha várias oportunidades ganhas.',
        date: 'Data de fechamento da oportunidade.',
        why:
            'O valor da oportunidade é o que o funil registrou, não a venda do financeiro. Para dinheiro, use a receita atribuída.',
    },
    {
        id: 'funnel',
        title: 'Funil',
        what:
            'Os leads de aquisição criados no período e até onde cada um foi: abriu oportunidade, ganhou oportunidade, comprou. Contado por LEAD, não por oportunidade: o mesmo lead pode ter várias. O lead criado pelo agendamento fica de fora: entraria com 100% de compra.',
        date:
            'Os leads são os do período. Oportunidade, ganho e compra valem ATÉ HOJE: um período passado continua amadurecendo, e o mês corrente sempre parece menor até os leads terem tempo de comprar.',
    },
    {
        id: 'attendance',
        title: 'Atendimento',
        what:
            'Conversas novas são contatos que escreveram pela primeira vez. Contatos que escreveram inclui os recorrentes. A primeira resposta mede da primeira mensagem do contato no período até a primeira resposta da empresa (IA ou equipe). "Sem resposta" é quem escreveu e ainda não recebeu resposta.',
        date: 'Data da mensagem do contato. Mapa de calor no horário de Brasília.',
        why: 'O tempo é a mediana: um contato esquecido por dois dias não distorce o número dos outros.',
    },
    {
        id: 'investment',
        title: 'Investimento, CPL, CAC e ROAS',
        what:
            'Investimento é o gasto que a Meta informa para os anúncios da conta conectada. CPL = investimento dividido pelos leads de anúncio que CHEGARAM ao sistema (não pelos que a Meta conta). CAC = investimento dividido pelos clientes de anúncio que compraram no período. ROAS = receita de anúncios dividida pelo investimento.',
        date:
            'Gasto pelo dia da conta de anúncio. A leitura roda de hora em hora e relê os últimos 3 dias, porque a Meta reatribui resultados; a primeira leitura carrega 90 dias. "Sincronizar agora" força a leitura.',
        why:
            'O CPL usa o que chegou porque é o custo real de um lead para a operação. Só a conta conectada por login na Meta tem o investimento: a conexão por pixel não lê a conta de anúncio. O investimento é da organização inteira, não de uma filial.',
    },
    {
        id: 'ads',
        title: 'Na plataforma x no sistema',
        what:
            '"Cliques" é quem tocou no botão do anúncio. "Na plataforma" é o que a Meta conta como resultado: leads de formulário e do site mais conversas iniciadas — e a Meta só conta a conversa quando a pessoa ENVIA a primeira mensagem. "Clique → conversa" mostra quanto dos cliques chegou a escrever. "No sistema" são os leads que chegaram aqui com o rastro do anúncio, e "Chegada" é a divisão deles pelo que a Meta contou.',
        date: 'Resultados da plataforma pelo dia da conta; leads pelo dia em que chegaram.',
        why:
            'Clique → conversa baixo é perda DENTRO do WhatsApp da pessoa: ela abriu e não escreveu, e ninguém recebe esse contato. A alavanca é o anúncio (mensagem pronta para só tocar em enviar, perguntas frequentes). Chegada baixa costuma ser: anúncio apontando para um número que não está na API oficial (o WhatsApp Web não recebe o rastro), formulário de Lead Ads sem a página integrada, ou contato que abriu a conversa e não escreveu. Lead que chega sem rastro aparece como WhatsApp orgânico.',
    },
]

interface MarketingHelpModalProps {
    topic: MarketingHelpTopic | null
    onClose: () => void
}

export default function MarketingHelpModal({ topic, onClose }: MarketingHelpModalProps) {
    const refs = useRef<Partial<Record<MarketingHelpTopic, HTMLElement | null>>>({})

    useEffect(() => {
        if (topic) refs.current[topic]?.scrollIntoView({ block: 'start', behavior: 'smooth' })
    }, [topic])

    useEffect(() => {
        if (!topic) return
        const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [topic, onClose])

    if (!topic) return null

    return (
        <div className="modal modal-open">
            <div className="modal-box max-w-2xl">
                <div className="flex items-start justify-between gap-3 mb-4">
                    <div className="flex items-center gap-2">
                        <MdHelpOutline className="text-xl text-secondary" />
                        <div>
                            <h3 className="text-lg font-semibold text-base-content">Como ler o dashboard</h3>
                            <p className="text-sm text-base-content/60">O que cada número soma, por qual data e por quê.</p>
                        </div>
                    </div>
                    <button className="btn btn-sm btn-ghost btn-circle" onClick={onClose} aria-label="Fechar">
                        <MdClose className="text-lg" />
                    </button>
                </div>

                <div className="flex flex-col gap-3 max-h-[65vh] overflow-y-auto pr-1">
                    {SECTIONS.map(section => {
                        const active = section.id === topic
                        return (
                            <section
                                key={section.id}
                                ref={el => {
                                    refs.current[section.id] = el
                                }}
                                className={`rounded-xl border p-4 flex flex-col gap-2 ${
                                    active ? 'border-secondary/60 bg-secondary/5' : 'border-base-300/60'
                                }`}
                            >
                                <h4 className="font-semibold text-base-content">{section.title}</h4>
                                <p className="text-sm text-base-content/80">{section.what}</p>
                                <p className="text-xs text-base-content/60">
                                    <span className="font-medium text-base-content/70">Data: </span>
                                    {section.date}
                                </p>
                                {section.why && (
                                    <p className="text-xs text-base-content/60">
                                        <span className="font-medium text-base-content/70">Por quê: </span>
                                        {section.why}
                                    </p>
                                )}
                            </section>
                        )
                    })}
                </div>
            </div>
            <div className="modal-backdrop" onClick={onClose} />
        </div>
    )
}
